import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Phone,
  Mail,
  User,
  Plus,
  Trash2,
  Loader2,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useClientContext } from '../ClientContext';
import { contactsApi, type ClientContactItem, type UploadedContactItem } from '@/api/contacts';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Неизвестная ошибка';
}

export function ContactsTab() {
  const { clientId, detailLoading } = useClientContext();
  const queryClient = useQueryClient();

  const [bindDialogOpen, setBindDialogOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<ClientContactItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUploaded, setSelectedUploaded] = useState<UploadedContactItem | null>(null);

  // Fetch client contacts
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['clientContacts', clientId],
    queryFn: () => contactsApi.getClientContacts(clientId),
    enabled: !!clientId,
  });

  // Fetch suggestions — временно отключено
  // const { data: suggestions } = useQuery({
  //   queryKey: ['contactSuggestions', clientId],
  //   queryFn: () => contactsApi.getSuggestions(clientId),
  //   enabled: !!clientId,
  // });

  // Search uploaded contacts
  const { data: searchResults, isFetching: isSearching } = useQuery({
    queryKey: ['uploadedContactsSearch', searchQuery],
    queryFn: () => contactsApi.searchUploaded(searchQuery),
    enabled: searchQuery.length >= 2,
  });

  // Bind mutation
  const bindMutation = useMutation({
    mutationFn: (params: { uploadedContactId: string; clientId: number }) =>
      contactsApi.bind(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientContacts', clientId] });
      queryClient.invalidateQueries({ queryKey: ['contactSuggestions', clientId] });
      setBindDialogOpen(false);
      setSelectedUploaded(null);
      setSearchQuery('');
      toast.success('Контакт привязан');
    },
    onError: (error) => {
      toast.error('Ошибка привязки', { description: getErrorMessage(error) });
    },
  });

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: (linkId: string) => contactsApi.unlinkContact(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientContacts', clientId] });
      setUnlinkTarget(null);
      toast.success('Контакт отвязан');
    },
    onError: (error) => {
      toast.error('Ошибка', { description: getErrorMessage(error) });
    },
  });

  // handleBindSuggestion — временно отключено
  // const handleBindSuggestion = (suggestion: ContactSuggestion) => {
  //   bindMutation.mutate({
  //     uploadedContactId: suggestion.uploadedContactId,
  //     clientId,
  //   });
  // };

  const handleBindUploaded = () => {
    if (!selectedUploaded) return;
    bindMutation.mutate({
      uploadedContactId: selectedUploaded.id,
      clientId,
    });
  };

  if (detailLoading || isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Contacts list */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium flex items-center gap-1.5">
            <User className="h-4 w-4" />
            Контакты ({contacts.length})
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBindDialogOpen(true)}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Добавить
          </Button>
        </div>

        {contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Нет привязанных контактов
          </p>
        ) : (
          contacts.map((contact) => (
            <div
              key={contact.id}
              className="p-3 rounded-lg border bg-card space-y-1.5"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{contact.displayName}</p>
                  {contact.position && (
                    <p className="text-xs text-muted-foreground">{contact.position}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => setUnlinkTarget(contact)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>

              {contact.phones.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {contact.phones.map((p, i) => (
                    <a
                      key={i}
                      href={`tel:${p.phone}`}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <Phone className="h-3 w-3" />
                      {p.phone}
                    </a>
                  ))}
                </div>
              )}

              {contact.emails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {contact.emails.map((e, i) => (
                    <a
                      key={i}
                      href={`mailto:${e.email}`}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <Mail className="h-3 w-3" />
                      {e.email}
                    </a>
                  ))}
                </div>
              )}

              <p className="text-[10px] text-muted-foreground">
                Привязал: {contact.linkedBy.name}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Bind dialog (search uploaded contacts) */}
      <Dialog open={bindDialogOpen} onOpenChange={setBindDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Привязать контакт</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по имени..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedUploaded(null);
                }}
                className="pl-10"
              />
            </div>

            {isSearching && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {searchResults && searchResults.items.length === 0 && searchQuery.length >= 2 && (
              <p className="text-sm text-muted-foreground text-center py-3">
                Ничего не найдено
              </p>
            )}

            {searchResults && searchResults.items.length > 0 && (
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {searchResults.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedUploaded(item)}
                    className={`w-full text-left p-2.5 rounded-md transition-colors ${
                      selectedUploaded?.id === item.id
                        ? 'bg-primary/10 border border-primary/30'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <p className="text-sm font-medium">{item.displayName}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.phones.map((p) => p.number).join(', ')}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      от: {item.employeeName}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBindDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleBindUploaded}
              disabled={!selectedUploaded || bindMutation.isPending}
            >
              {bindMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Привязать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink confirmation */}
      <AlertDialog open={!!unlinkTarget} onOpenChange={(open) => !open && setUnlinkTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отвязать контакт?</AlertDialogTitle>
            <AlertDialogDescription>
              Контакт «{unlinkTarget?.displayName}» будет отвязан от этого клиента.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => unlinkTarget && unlinkMutation.mutate(unlinkTarget.linkId)}
              disabled={unlinkMutation.isPending}
            >
              {unlinkMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Отвязать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
